import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    writeBatch,
    arrayUnion,
    arrayRemove
} from 'firebase/firestore';
import { db } from './firebase';
import { Tag } from '@/types/tags';

export const twitterTagSyncService = {
    /**
     * Syncs Twitter handle associations for a tag.
     * Updates TwitterHandle.linkedTags (IDs) and Tweet.linkedTags (Slugs).
     * 
     * @param tagId The ID of the tag being updated
     * @param newHandleIds The list of Twitter handle IDs that should be linked to this tag
     */
    async syncTagHandles(tagId: string, newHandleIds: string[]) {
        if (!db) throw new Error('Firebase not initialized');

        // 1. Get the tag to retrieve its slug
        const tagRef = doc(db, 'tags', tagId);
        const tagSnap = await getDoc(tagRef);

        if (!tagSnap.exists()) {
            throw new Error(`Tag not found: ${tagId}`);
        }

        const tag = tagSnap.data() as Tag;
        const tagSlug = tag.slug;

        // 2. Find currently linked handles to determine additions and removals
        // We query handles that have this tagId in their linkedTags
        const currentHandlesQuery = query(
            collection(db, 'twitterHandles'),
            where('linkedTags', 'array-contains', tagId)
        );

        const currentHandlesSnap = await getDocs(currentHandlesQuery);
        const currentHandleIds = currentHandlesSnap.docs.map(doc => doc.id);

        const handlesToAdd = newHandleIds.filter(id => !currentHandleIds.includes(id));
        const handlesToRemove = currentHandleIds.filter(id => !newHandleIds.includes(id));

        if (handlesToAdd.length === 0 && handlesToRemove.length === 0) {
            return; // No changes needed
        }

        console.log(`Syncing tag ${tag.name} (${tagId}): Adding to ${handlesToAdd.length} handles, Removing from ${handlesToRemove.length} handles`);

        // 3. Process Additions
        for (const handleId of handlesToAdd) {
            await this.linkHandleToTag(handleId, tagId, tagSlug);
        }

        // 4. Process Removals
        for (const handleId of handlesToRemove) {
            await this.unlinkHandleFromTag(handleId, tagId, tagSlug);
        }
    },

    /**
     * Links a handle to a tag:
     * - Adds tagId to TwitterHandle.linkedTags
     * - Adds tagSlug to all Tweets from this handle
     */
    async linkHandleToTag(handleId: string, tagId: string, tagSlug: string) {
        if (!db) throw new Error('Firebase not initialized');

        const batch = writeBatch(db);

        // Update Handle
        const handleRef = doc(db, 'twitterHandles', handleId);
        batch.update(handleRef, {
            linkedTags: arrayUnion(tagId)
        });

        // Update Tweets (Batching handled if > 500 tweets, though unlikely for now)
        // We'll fetch all tweets for this handle
        const tweetsQuery = query(
            collection(db, 'tweets'),
            where('handleId', '==', handleId)
        );

        const tweetsSnap = await getDocs(tweetsQuery);

        // Firestore batch limit is 500. We have 1 op for handle, so 499 left for tweets.
        // If more, we need multiple batches.
        let operationCount = 1;
        let currentBatch = batch;

        for (const tweetDoc of tweetsSnap.docs) {
            if (operationCount >= 499) {
                await currentBatch.commit();
                currentBatch = writeBatch(db);
                operationCount = 0;
            }

            currentBatch.update(tweetDoc.ref, {
                linkedTags: arrayUnion(tagSlug)
            });
            operationCount++;
        }

        await currentBatch.commit();
    },

    /**
     * Unlinks a handle from a tag:
     * - Removes tagId from TwitterHandle.linkedTags
     * - Removes tagSlug from all Tweets from this handle
     */
    async unlinkHandleFromTag(handleId: string, tagId: string, tagSlug: string) {
        if (!db) throw new Error('Firebase not initialized');

        const batch = writeBatch(db);

        // Update Handle
        const handleRef = doc(db, 'twitterHandles', handleId);
        batch.update(handleRef, {
            linkedTags: arrayRemove(tagId)
        });

        // Update Tweets
        const tweetsQuery = query(
            collection(db, 'tweets'),
            where('handleId', '==', handleId)
        );

        const tweetsSnap = await getDocs(tweetsQuery);

        let operationCount = 1;
        let currentBatch = batch;

        for (const tweetDoc of tweetsSnap.docs) {
            if (operationCount >= 499) {
                await currentBatch.commit();
                currentBatch = writeBatch(db);
                operationCount = 0;
            }

            currentBatch.update(tweetDoc.ref, {
                linkedTags: arrayRemove(tagSlug)
            });
            operationCount++;
        }

        await currentBatch.commit();
    }
};
